/**
 * NTD 홈페이지 랜딩 페이지
 * NTD - 비즈니스 시스템 설계
 * 목적: 방문자 → 문의/도입 신청으로 전환
 */

import React, { useState } from 'react';
import {
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  FaceSmileIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  ClockIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  HomeModernIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowRightIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

const LandingPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    facilityType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // 적용 가능 업종
  const industries = [
    { icon: BookOpenIcon, name: '독서실', desc: '학생 출입 관리' },
    { icon: AcademicCapIcon, name: '학원', desc: '수강생 출석 체크' },
    { icon: UserGroupIcon, name: '헬스장', desc: '회원 출입 관리' },
    { icon: BuildingOfficeIcon, name: '사무실', desc: '직원 출퇴근 관리' },
    { icon: HomeModernIcon, name: '스터디카페', desc: '이용자 출입 기록' },
    { icon: BuildingStorefrontIcon, name: '공유오피스', desc: '입주자 출입 관리' },
  ];

  // 문제 해결 포인트
  const problems = [
    { problem: '카드 분실/재발급 비용', solution: '얼굴 기반 인증으로 분실·복제 불가' },
    { problem: '비밀번호 공유 문제', solution: '생체인증으로 대리출입 원천 차단' },
    { problem: '출입 기록 누락', solution: '자동 기록 저장 (100% 정확도)' },
    { problem: '학부모 문의 대응 부담', solution: '카카오 알림톡 자동 발송' },
  ];

  // FAQ
  const faqs = [
    {
      question: '마스크를 쓰면 인식되나요?',
      answer: '마스크 착용 시 정확도가 낮아질 수 있습니다. 입구에서 잠시 마스크를 내리고 인식하는 것을 권장합니다.',
    },
    {
      question: '자동문 연동은 가능한가요?',
      answer: '네, 가능합니다. 자동문, 전기식 도어락 등과 연동하여 얼굴 인식 후 자동 개방까지 완전 무인 운영이 가능합니다. (설치가 필요한 옵션)',
    },
    {
      question: '태블릿도 사용 가능한가요?',
      answer: '네, 안드로이드 기반 기기라면 스마트폰, 태블릿 모두 사용 가능합니다.',
    },
    {
      question: '인터넷이 안 되는 환경에서도 동작하나요?',
      answer: '인터넷 연결이 필수입니다. Wi-Fi 또는 모바일 데이터(LTE/5G)가 연결되어 있어야 정상 동작합니다.',
    },
    {
      question: '기존 회원 데이터를 이전할 수 있나요?',
      answer: '네, 엑셀 파일로 기존 회원 데이터를 일괄 등록할 수 있습니다. 도입 시 무료로 데이터 이전을 도와드립니다.',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO: 실제 API 연동
    // 현재는 시뮬레이션
    await new Promise(resolve => setTimeout(resolve, 1000));

    setSubmitSuccess(true);
    setIsSubmitting(false);

    // 3초 후 초기화
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({ name: '', phone: '', facilityType: '', message: '' });
    }, 3000);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 헤더 - 상단 고정 */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* 로고 - 왼쪽 */}
            <div className="flex items-center gap-3 cursor-pointer flex-shrink-0 mr-8" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img
                alt="NTD 로고"
                className="rounded-lg"
                src="/logo-ntd.png"
                style={{ width: '36px', height: '36px', objectFit: 'contain' }}
              />
              <span className="text-xl font-bold text-gray-900 whitespace-nowrap">NTD</span>
            </div>

            {/* 네비게이션 - 가운데 (flex-1로 남은 공간 차지) */}
            <nav className="hidden md:flex items-center space-x-8 flex-1">
              <button onClick={() => scrollToSection('face-recognition')} className="text-blue-600 hover:text-blue-800 text-sm font-bold whitespace-nowrap">
                AI 얼굴인식
              </button>
              <button onClick={() => scrollToSection('solutions')} className="text-gray-600 hover:text-gray-900 text-sm font-medium whitespace-nowrap">
                솔루션
              </button>
              <button onClick={() => scrollToSection('industries')} className="text-gray-600 hover:text-gray-900 text-sm font-medium whitespace-nowrap">
                적용 분야
              </button>
              <button onClick={() => scrollToSection('process')} className="text-gray-600 hover:text-gray-900 text-sm font-medium whitespace-nowrap">
                진행 과정
              </button>
              <button onClick={() => scrollToSection('cases')} className="text-gray-600 hover:text-gray-900 text-sm font-medium whitespace-nowrap">
                설치사례
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-600 hover:text-gray-900 text-sm font-medium whitespace-nowrap">
                FAQ
              </button>
            </nav>

            {/* CTA 버튼 - 오른쪽 */}
            <div className="flex-shrink-0 ml-8">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold shadow-sm whitespace-nowrap"
              >
                도입 상담 요청
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Above the fold */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* 메인 헤드라인 */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="text-blue-600">수기 출석부</span>는 이제 그만,
              <br />
              출결을 자동화하세요
            </h1>

            {/* 서브 헤드라인 */}
            <p className="text-xl text-gray-600 mb-4">
              AI 얼굴인식으로 출석부 작성부터 학부모 알림까지 — 모두 자동으로 처리됩니다
            </p>

            {/* 핵심 가치 한 줄 */}
            <p className="text-lg text-blue-700 font-medium mb-8">
              자동 출석부 · AI 얼굴인식 · 카카오 알림톡 · 자동문 연동
            </p>

            {/* CTA 버튼 */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                무료 상담 요청하기
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollToSection('solutions')}
                className="px-8 py-4 bg-white text-gray-700 text-lg font-semibold rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
              >
                솔루션 둘러보기
              </button>
            </div>

            {/* 핵심 장점 3가지 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border-2 border-blue-100">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaceSmileIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">AI 얼굴인식</p>
                  <p className="text-sm text-gray-500">카드·QR 필요 없음</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShieldCheckIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">대리출입 차단</p>
                  <p className="text-sm text-gray-500">생체인증으로 완벽 보안</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CogIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">무인 운영</p>
                  <p className="text-sm text-gray-500">자동문 연동 가능</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI 얼굴인식 강조 섹션 */}
      <section id="face-recognition" className="py-20 bg-gradient-to-b from-gray-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
                <SparklesIcon className="w-4 h-4" />
                핵심 기술
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight">
                카드도, 비밀번호도<br />
                <span className="text-blue-400">이제 필요 없습니다</span>
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                NewAttend의 AI 얼굴인식 기술로 카메라 앞에 서기만 하면 1~1.5초 만에 본인 확인이 완료됩니다.
                카드 분실, 비밀번호 공유, 대리 출입 문제를 원천 차단합니다.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-3xl font-bold text-blue-400 mb-1">1~1.5초</p>
                  <p className="text-sm text-gray-400">인식 속도</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-3xl font-bold text-blue-400 mb-1">99%+</p>
                  <p className="text-sm text-gray-400">인식 정확도</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-3xl font-bold text-blue-400 mb-1">0원</p>
                  <p className="text-sm text-gray-400">카드 발급 비용</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-3xl font-bold text-blue-400 mb-1">24시간</p>
                  <p className="text-sm text-gray-400">무인 운영 가능</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl p-8 backdrop-blur-sm border border-white/10">
                <div className="text-center mb-8">
                  <div className="w-24 h-24 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaceSmileIcon className="w-14 h-14 text-blue-300" />
                  </div>
                  <p className="text-xl font-bold">AI 얼굴인식 출입</p>
                  <p className="text-sm text-gray-400 mt-1">태블릿 하나로 시작</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">카드 없이 얼굴만으로 입실·퇴실</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">출입 시 학부모에게 카카오 알림톡 자동 발송</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">자동문·도어락 연동으로 완전 무인 운영</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">모든 출입 기록 자동 저장 및 통계</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 실제 출석부 화면 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              <DocumentTextIcon className="w-4 h-4" />
              실제 운영 화면
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              수기 출석부는 잊으세요
            </h2>
            <p className="text-lg text-gray-600">
              70명의 출결 현황을 한 화면에서 실시간으로 확인합니다
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img
              src="/screenshot-attendance.png"
              alt="NewAttend 출석부 실시간 관리 화면 - 출석, 외출, 미출석, 곧 입실 상태를 한눈에 확인"
              className="w-full"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-8">
            <div className="text-center p-3 bg-white rounded-xl shadow-sm">
              <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
              <p className="text-sm font-medium text-gray-700">출석</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl shadow-sm">
              <div className="w-3 h-3 bg-orange-400 rounded-full mx-auto mb-2"></div>
              <p className="text-sm font-medium text-gray-700">곧 입실</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl shadow-sm">
              <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-2"></div>
              <p className="text-sm font-medium text-gray-700">외출</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl shadow-sm">
              <div className="w-3 h-3 bg-red-500 rounded-full mx-auto mb-2"></div>
              <p className="text-sm font-medium text-gray-700">미출석</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl shadow-sm">
              <div className="w-3 h-3 bg-gray-300 rounded-full mx-auto mb-2"></div>
              <p className="text-sm font-medium text-gray-700">스케줄 없음</p>
            </div>
          </div>
        </div>
      </section>

      {/* 솔루션 섹션 */}
      <section id="solutions" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              NTD가 제공하는 솔루션
            </h2>
            <p className="text-lg text-gray-600">
              비즈니스 환경에 맞는 맞춤형 시스템을 설계합니다
            </p>
          </div>

          {/* 솔루션 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 출입 관리 솔루션 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <FaceSmileIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">출입 관리 솔루션</h3>
              <p className="text-gray-600 mb-4">
                얼굴인식 기반 출입 관리 시스템으로 카드/QR 없이 자동 인증
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                  얼굴 인식 출입
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                  자동문/도어락 연동
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                  출입 기록 자동 저장
                </li>
              </ul>
            </div>

            {/* 회원 관리 솔루션 */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <UserGroupIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">회원 관리 솔루션</h3>
              <p className="text-gray-600 mb-4">
                회원 정보, 이용권, 결제까지 통합 관리하는 시스템
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-purple-500" />
                  회원 정보 관리
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-purple-500" />
                  이용권/결제 관리
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-purple-500" />
                  통계 및 리포트
                </li>
              </ul>
            </div>

            {/* 알림 자동화 솔루션 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <BellAlertIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">알림 자동화 솔루션</h3>
              <p className="text-gray-600 mb-4">
                카카오 알림톡 연동으로 고객/학부모에게 자동 알림 발송
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  입실/퇴실 알림
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  미출석 알림
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  공지사항 발송
                </li>
              </ul>
            </div>

            {/* 좌석 관리 솔루션 */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <BuildingOfficeIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">좌석 관리 솔루션</h3>
              <p className="text-gray-600 mb-4">
                실시간 좌석 현황 파악 및 배치도 기반 좌석 관리
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-orange-500" />
                  실시간 좌석 현황
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-orange-500" />
                  좌석 배치도 설정
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-orange-500" />
                  좌석별 이용 통계
                </li>
              </ul>
            </div>

            {/* 출석부 자동화 솔루션 */}
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-pink-600 rounded-2xl flex items-center justify-center mb-6">
                <DocumentTextIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">출석부 자동화</h3>
              <p className="text-gray-600 mb-4">
                수기 출석부를 프로그램으로 대체 — 출결 기록이 자동으로 쌓입니다
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-pink-500" />
                  얼굴인식 출석 자동 기록
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-pink-500" />
                  일별/주별/월별 출결 통계
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-pink-500" />
                  학부모 출결 리포트
                </li>
              </ul>
            </div>

            {/* 맞춤 개발 */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300">
              <div className="w-16 h-16 bg-gray-600 rounded-2xl flex items-center justify-center mb-6">
                <CogIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">맞춤 개발</h3>
              <p className="text-gray-600 mb-4">
                비즈니스에 딱 맞는 시스템이 필요하신가요? 맞춤 설계해 드립니다
              </p>
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium"
              >
                문의하기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 적용 분야 섹션 */}
      <section id="industries" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              다양한 분야에 맞춤 설계
            </h2>
            <p className="text-lg text-gray-600">
              업종에 상관없이, 비즈니스에 필요한 시스템을 설계합니다
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <industry.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{industry.name}</h3>
                <p className="text-sm text-gray-500">{industry.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 자동문 연동 옵션 섹션 */}
      <section id="auto-door" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <SparklesIcon className="w-4 h-4" />
                확장 기능
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                원한다면 자동문·도어락과도<br />연동됩니다
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                얼굴 인식 후 자동 개방까지 — <span className="text-blue-600 font-semibold">완전 무인 출입 운영</span>이 가능합니다.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">자동문, 전기식 도어락, 스피드게이트 연동</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">얼굴 인식 성공 시 자동 개방</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">24시간 무인 운영 가능</span>
                </li>
              </ul>
              <p className="mt-6 text-sm text-gray-500 bg-gray-100 rounded-lg px-4 py-3">
                * 시설 구조에 따라 설치가 필요한 옵션입니다. 상담 시 자세한 안내를 받으실 수 있습니다.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 text-white">
                <div className="flex items-center gap-4 mb-6">
                  <CogIcon className="w-12 h-12" />
                  <div>
                    <p className="text-blue-200 text-sm">자동문 연동 시</p>
                    <p className="text-2xl font-bold">완전 무인 운영</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <span>회원이 입실/퇴실 버튼 터치</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <span>얼굴 인식으로 본인 확인</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    <span>인증 성공 시 문 자동 개방</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                    <span>출입 기록 + 알림 자동 발송</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 문제 해결 섹션 */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              이런 문제, 겪고 계신가요?
            </h2>
            <p className="text-lg text-gray-400">
              NTD가 해결해드립니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {problems.map((item, index) => (
              <div key={index} className="bg-gray-800 rounded-2xl p-6">
                {/* 문제 - X 아이콘과 텍스트 */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400 font-bold">X</span>
                  </div>
                  <p className="text-gray-400 line-through">{item.problem}</p>
                </div>
                {/* 연결선 */}
                <div className="w-px h-6 bg-gray-700 ml-5"></div>
                {/* 해결책 - 체크 아이콘과 텍스트 */}
                <div className="flex items-center gap-4 mt-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircleIcon className="w-5 h-5 text-green-400" />
                  </div>
                  <p className="text-white font-medium">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 진행 과정 섹션 */}
      <section id="process" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              진행 과정
            </h2>
            <p className="text-lg text-gray-600">
              비즈니스에 맞는 시스템을 단계별로 설계해 드립니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="relative">
              <div className="bg-blue-50 rounded-2xl p-6 text-center h-full">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="font-bold text-gray-900 mb-2">상담 요청</h3>
                <p className="text-sm text-gray-600">현재 업무 환경과<br />필요한 기능 파악</p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                <ArrowRightIcon className="w-6 h-6 text-gray-300" />
              </div>
            </div>

            <div className="relative">
              <div className="bg-blue-50 rounded-2xl p-6 text-center h-full">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="font-bold text-gray-900 mb-2">요구사항 분석</h3>
                <p className="text-sm text-gray-600">업무 프로세스 분석 및<br />맞춤 솔루션 제안</p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                <ArrowRightIcon className="w-6 h-6 text-gray-300" />
              </div>
            </div>

            <div className="relative">
              <div className="bg-blue-50 rounded-2xl p-6 text-center h-full">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="font-bold text-gray-900 mb-2">시스템 구축</h3>
                <p className="text-sm text-gray-600">맞춤형 시스템<br />개발 및 설정</p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                <ArrowRightIcon className="w-6 h-6 text-gray-300" />
              </div>
            </div>

            <div>
              <div className="bg-green-50 rounded-2xl p-6 text-center h-full border-2 border-green-200">
                <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="font-bold text-gray-900 mb-2">도입 완료!</h3>
                <p className="text-sm text-gray-600">교육 및 지속적인<br />운영 지원</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 설치사례 섹션 */}
      <section id="cases" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              설치 사례
            </h2>
            <p className="text-lg text-gray-600">
              이미 현장에서 사용하고 계십니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* 대구 고등학교 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <AcademicCapIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">대구 소재 고등학교</h3>
                    <p className="text-blue-200 text-sm">교육기관 | 학생 출결 관리</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-5">
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">얼굴인식 출입</span>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">출결 관리</span>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">학생 관리</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-600 text-lg">"</span>
                    </div>
                    <div>
                      <p className="text-gray-700 leading-relaxed">
                        체계적으로 보이고, <span className="font-semibold text-gray-900">한눈에 관리할 수 있어서 너무 좋습니다.</span>
                      </p>
                      <p className="text-sm text-gray-500 mt-2">— 교감 선생님</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 광교 독서실 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <BookOpenIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">광교 소재 독서실</h3>
                    <p className="text-purple-200 text-sm">독서실 | 회원 출결 관리</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-5">
                  <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">얼굴인식 출입</span>
                  <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">출석부 자동화</span>
                  <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">회원 관리</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-600 text-lg">"</span>
                    </div>
                    <div>
                      <p className="text-gray-700 leading-relaxed">
                        항상 수기로 출석부를 작성했는데, 프로그램으로 하니 <span className="font-semibold text-gray-900">일손이 많이 줄었습니다.</span>
                      </p>
                      <p className="text-sm text-gray-500 mt-2">— 독서실 원장님</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 요금제 섹션 */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              합리적인 요금제
            </h2>
            <p className="text-lg text-gray-600">
              초기 설치 + 월 33,000원으로 시작하세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* 초기 비용 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div className="p-8 bg-gray-50">
                <h3 className="text-xl font-bold text-gray-900 mb-2">초기 비용</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-gray-900">별도 문의</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">가맹비 + 설치비 (1회)</p>
              </div>
              <div className="p-8">
                <ul className="space-y-4">
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>현장 방문 설치</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>태블릿 + 거치대 세팅</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>초기 회원 데이터 등록</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>사용법 교육</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>게이트 연동 (선택)</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 월 이용료 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-blue-500 relative">
              <div className="p-8 bg-blue-50">
                <h3 className="text-xl font-bold text-gray-900 mb-2">월 이용료</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-blue-600">33,000</span>
                  <span className="text-gray-600">원/월</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">전체 기능 + 부가서비스 (부가세 포함)</p>
              </div>
              <div className="p-8">
                <ul className="space-y-4">
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>AI 얼굴인식 출입</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>회원 / 출결 / 좌석 / 이용권 관리</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm font-medium text-blue-700">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>외부 접속 지원 (어디서든 관리)</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm font-medium text-blue-700">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>학부모 페이지 (출결 확인)</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm font-medium text-blue-700">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>DB 자동 백업</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm font-medium text-blue-700">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>카카오 알림톡 (월 1,000건 기본)</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm font-medium text-blue-700">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>자동 업데이트</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm mb-4">
              * 알림톡 월 1,000건 기본 제공, 추가 발송 시 건당 13원
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
            >
              도입 상담 신청
            </button>
          </div>
        </div>
      </section>

      {/* FAQ 섹션 */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              자주 묻는 질문
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 flex items-center gap-3">
                    <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 문의/신청 섹션 */}
      <section id="contact" className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              도입 상담 요청
            </h2>
            <p className="text-lg text-blue-100">
              시설에 맞는 맞춤 견적과 상담을 받아보세요
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            {submitSuccess ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">상담 요청이 접수되었습니다!</h3>
                <p className="text-gray-600">빠른 시일 내에 연락드리겠습니다.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="홍길동"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      연락처 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="010-1234-5678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    시설 유형 <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.facilityType}
                    onChange={(e) => setFormData({ ...formData, facilityType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">선택해주세요</option>
                    <option value="독서실">독서실</option>
                    <option value="학원">학원</option>
                    <option value="헬스장">헬스장</option>
                    <option value="사무실">사무실</option>
                    <option value="스터디카페">스터디카페</option>
                    <option value="공유오피스">공유오피스</option>
                    <option value="기타">기타</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    문의 내용 (선택)
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="궁금한 점이 있으시면 남겨주세요"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-blue-600 text-white text-lg font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      처리 중...
                    </>
                  ) : (
                    <>
                      도입 상담 요청하기
                      <ArrowRightIcon className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* 직접 연락 */}
          <div className="mt-8 text-center">
            <p className="text-blue-100 mb-3">바로 상담을 원하시면</p>
            <a
              href="tel:010-9042-1900"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
            >
              <PhoneIcon className="w-5 h-5" />
              <span className="font-semibold">010-9042-1900</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  alt="NTD 로고"
                  className="rounded-lg"
                  src="/logo-ntd.png"
                  style={{ width: '32px', height: '32px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                />
                <span className="text-xl font-bold">NTD</span>
              </div>
              <p className="text-gray-400">
                비즈니스에 필요한 시스템을<br />
                맞춤 설계합니다
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">바로가기</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('solutions')} className="hover:text-white">솔루션</button></li>
                <li><button onClick={() => scrollToSection('industries')} className="hover:text-white">적용 분야</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white">FAQ</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white">도입 문의</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">문의</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <PhoneIcon className="w-4 h-4" />
                  010-9042-1900
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; 2026 NTD. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
